import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete,
  Box,
  Card,
  AlertColor,
  Alert,
  CardHeader,
  CardContent
} from '@mui/material';

import { useOrganizationActions, useSelectedOrganization } from 'shared/store/organization';

import { IOrganization } from 'shared/types';
import { useSessionActions } from 'shared/store/session';


export const OrganizationSeletion:React.FC = () => {
  const userActions = useSessionActions();
  const actions = useOrganizationActions();
  const selectedOrganization = useSelectedOrganization();
  const [selectedItem, setSelectedItem] = useState<IOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [orgs, setOrgs] = useState<IOrganization[]>([]);
  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { t } = useTranslation();

  const navigate = useNavigate();

  React.useEffect(() => {
    setIsLoading(true);
    actions.getOrganizations()
      .then(result => {
        if (!Array.isArray(result)) {
          setAlert({
            text: t('messages.organizationSeletion.noContent'),
            type: 'error',
          });
        } else {
          setOrgs(result);
          if (result.length == 1) {
            actions.setSelectedOrganization(result[0]);
          }
        }
      })
      .catch(() => {
        setAlert({
          text: t('global.error'),
          type: 'error',
        });
      });
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    if (selectedOrganization) {
      navigate('/');
    }
  }, [selectedOrganization]);

  const handleConfirmSelection = () => {
    actions.setSelectedOrganization(selectedItem);
  };

  const handleLogout = async () => {
    try {
      await userActions.logout();
    } catch (e) {
      // ignora erro no logout, vai ser deslogado de qualquer forma
    } finally {
      navigate('/login');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        width: '450px',
        margin: '0 auto',
      }}>
      <Card sx={{ marginTop: '80px' }}>
        <CardHeader sx={{ textAlign: 'center', padding: '16px', color: '#153085' }} title={t('pages.organizationSeletion.organization')} />

        <CardContent>
          <div>
            {alert ? (
              <>
                <Grid item xs={12}>
                  <Alert severity={alert.type} onClose={() => setAlert(null)}>
                    {alert.text}
                  </Alert>
                </Grid>
              </>
            ) : (
              <Grid
                item
                xs={12}
                component={Typography}
              >
                {t('messages.organizationSeletion.chooseOrg')}
              </Grid>
            )}
          </div>
          <Box
            sx={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              alignItems: 'center',
              justifyContent: 'stretch',
              pt: 6,
              px: 4,
            }}
          >
            {isLoading && <CircularProgress />}
            {orgs && (
              <>
                <Autocomplete
                  fullWidth
                  options={orgs}
                  clearOnBlur
                  getOptionLabel={(option) => (option as IOrganization).OrganizacaoNome}
                  value={selectedItem}
                  onChange={(_, newValue) => {
                    setSelectedItem(newValue as IOrganization);
                    localStorage.setItem('org', newValue!.CodigoOrganizacao.toString());
                  }}
                  noOptionsText={t('messages.organizationSeletion.notFound')}
                  renderInput={(params) => <TextField {...params} placeholder={t('messages.organizationSeletion.selectOrg')} />}
                  renderOption={(props, option) => (
                    <li {...props} key={option.CodigoOrganizacao}>
                      <strong style={{ marginRight: '0.6rem' }}>{option.CodigoOrganizacao}</strong> {option.OrganizacaoNome}
                    </li>
                  )}
                />
                <Grid container spacing={2}>
                  <Grid container item xs={6}>
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      color="primary"
                      onClick={handleLogout}
                    >
                      {t('pages.organizationSeletion.exit')}
                    </Button>
                  </Grid>
                  <Grid container item xs={6}>
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      color="primary"
                      disabled={!selectedItem}
                      onClick={handleConfirmSelection}
                    >
                      {t('pages.organizationSeletion.confirm')}
                    </Button>
                  </Grid>
                </Grid>

              </>
            )}
          </Box>
        </CardContent>

      </Card>
    </Box>
  );
};